/**
 * CategoryHelpers.js - Utility functions for category detection and mapping
 * Extracted from CategoryPage.jsx to improve maintainability
 */

// Fashion & Beauty category slugs
export const FASHION_SLUGS = [
  'fashion-beauty',
  'womens-clothing',
  'mens-clothing',
  'kids-clothing',
  'traditional-wear',
  'shoes-footwear',
  'bags-accessories',
  'jewellery-watches',
  'beauty-skincare',
  'beauty-personal-care',
  'perfumes-cosmetics',
];

// Clothing category slugs (subset of fashion - shows laundry services)
export const CLOTHING_SLUGS = [
  'fashion-beauty',
  'womens-clothing',
  'mens-clothing',
  'kids-clothing',
  'traditional-wear',
];

// Sports, Fitness & Hobbies slugs
export const SPORTS_SLUGS = [
  'sports-fitness',
  'sports-fitness-hobbies',
  'sports-equipment',
  'fitness-gym',
  'outdoor-camping',
  'cycling-equipment',
  'fishing-hunting',
  'yoga-wellness',
  'board-games-puzzles',
  'creative-hobbies',
];

// Services & Trades slugs
export const TRADES_SERVICES_SLUGS = [
  'services-trades',
  'home-repairs-maintenance',
  'plumbing-electrical',
  'construction-masonry',
  'cleaning-services',
  'moving-removal',
  'pest-control',
  'security-services',
  'tailoring-alterations',
  // LK-only subcategories
  'ac-refrigeration',
  'solar-power',
  'water-pumps',
  'aluminium-glass',
  'welding-fabrication',
];

// Business & Industry slugs
export const BUSINESS_SLUGS = [
  'business-industry',
  'machinery-equipment',
  'industrial-tools',
  'manufacturing-supplies',
  'office-equipment',
  'wholesale-bulk-goods',
  'logistics-transport',
  'franchises',
  'business-for-sale',
];

// Food & Agriculture slugs
export const FOOD_AGRICULTURE_SLUGS = [
  'food-agriculture',
  'fresh-produce',
  'dairy-eggs',
  'meat-poultry',
  'seafood',
  'processed-foods',
  'bakery-confectionery',
  'beverages',
  'organic-specialty',
  'bulk-wholesale',
  'farm-supplies',
  'agricultural-equipment',
  'seeds-plants',
  'livestock',
  'catering-services',
  'food-trucks-stalls',
];

// Education & Tuition slugs
export const EDUCATION_SLUGS = [
  'education-tuition',
  'school-tuition',
  'university-coaching',
  'professional-courses',
  'language-learning',
  'music-arts-education',
  'stem-coding',
  'exam-preparation',
  'special-needs-education',
  'adult-education',
  'tutoring-services',
];

// Baby & Kids slugs
export const BABY_KIDS_SLUGS = [
  'baby-kids',
  'baby-kids-services',
  'baby-gear',
  'kids-furniture',
  'kids-clothing-accessories',
  'toys-games',
  'feeding-nursing',
  'kids-education-toys',
  'maternity-products',
  'childcare-services',
  'kids-party-services',
  'kids-health-safety',
];

// Pets & Animals slugs
export const PETS_ANIMALS_SLUGS = [
  'pets-animals',
  'pet-adoption',
  'pet-supplies',
  'pet-services',
  'pet-food',
  'pet-health',
  'aquarium-fish',
  'birds',
  'livestock-poultry',
  'pet-accessories',
];

// Musical Instruments slugs
export const MUSICAL_INSTRUMENTS_SLUGS = [
  'guitars',
  'keyboards-pianos',
  'drums-percussion',
  'string-instruments',
  'wind-instruments',
  'traditional-instruments',
  'dj-equipment',
  'amplifiers-effects',
  'music-accessories',
  'sheet-music',
  'musical-instruments',
  'audio-equipment',
  'recording-gear',
  'lessons-tuition',
  'repairs-servicing',
  'rentals-hire',
];

// RFQ-enabled subcategories for Business & Industry
export const RFQ_ENABLED_BUSINESS = [
  'machinery-equipment',
  'industrial-tools',
  'manufacturing-supplies',
  'office-equipment',
  'wholesale-bulk-goods',
];

// RFQ-enabled subcategories for Food & Agriculture
export const RFQ_ENABLED_FOOD_AGRI = [
  'fresh-produce',
  'dairy-eggs',
  'meat-poultry',
  'seafood',
  'processed-foods',
  'bulk-wholesale',
  'farm-supplies',
];

// Vehicle type mapping
export const VEHICLE_TYPE_MAP = {
  'vehicles': 'cars',
  'cars': 'cars',
  'vans': 'vans',
  'buses': 'buses',
  'motorcycles-scooters': 'motorcycles-scooters',
  'electric-bikes': 'electric-bikes',
  'trucks-lorries': 'trucks-lorries',
  'farm-vehicles': 'farm-vehicles',
  'plant-construction': 'plant-construction',
  'motorhomes': 'motorhomes',
  'caravans': 'caravans',
  'boats-watercraft': 'boats-watercraft',
  'three-wheelers': 'three-wheelers',
};

// Category detection functions
export const isFashionCategory = (slug) => FASHION_SLUGS.includes(slug?.toLowerCase());
export const isClothingCategory = (slug) => CLOTHING_SLUGS.includes(slug?.toLowerCase());
export const isJewelleryCategory = (slug) => slug?.toLowerCase() === 'jewellery-watches';
export const isSportsCategory = (slug) => SPORTS_SLUGS.includes(slug?.toLowerCase());
export const isServicesTradesCategory = (slug) => TRADES_SERVICES_SLUGS.includes(slug?.toLowerCase());
export const isBusinessIndustryCategory = (slug) => BUSINESS_SLUGS.includes(slug?.toLowerCase());
export const isFoodAgricultureCategory = (slug) => FOOD_AGRICULTURE_SLUGS.includes(slug?.toLowerCase());
export const isEducationCategory = (slug) => EDUCATION_SLUGS.includes(slug?.toLowerCase());
export const isBabyKidsCategory = (slug) => BABY_KIDS_SLUGS.includes(slug?.toLowerCase());
export const isPetsAnimalsCategory = (slug) => PETS_ANIMALS_SLUGS.includes(slug?.toLowerCase());
export const isMusicalInstrumentsCategory = (slug) => MUSICAL_INSTRUMENTS_SLUGS.includes(slug?.toLowerCase());

// RFQ detection
export const isRFQEnabledBusiness = (slug) => RFQ_ENABLED_BUSINESS.includes(slug?.toLowerCase());
export const isRFQEnabledFoodAgri = (slug) => RFQ_ENABLED_FOOD_AGRI.includes(slug?.toLowerCase());

// Get vehicle type from slug
export const getVehicleType = (slug) => VEHICLE_TYPE_MAP[slug?.toLowerCase()] || slug;

// Musical instruments subcategory mapping
export const getMusicSubcategoryMap = () => ({
  'guitars': 'guitars',
  'keyboards-pianos': 'keyboards-pianos',
  'drums-percussion': 'drums-percussion',
  'string-instruments': 'string-instruments',
  'wind-instruments': 'wind-instruments',
  'traditional-instruments': 'traditional-instruments',
  'dj-equipment': 'dj-equipment',
  'amplifiers-effects': 'amplifiers-effects',
  'music-accessories': 'music-accessories',
  'sheet-music': 'sheet-music',
  'audio-equipment': 'audio-equipment',
  'recording-gear': 'recording-gear',
  'lessons-tuition': 'lessons-tuition',
  'repairs-servicing': 'repairs-servicing',
  'rentals-hire': 'rentals-hire',
  'musical-instruments': null,
});

// Get subcategory mappings
export const getTradesSubcategoryMap = () => ({
  'services-trades': null,
  'home-repairs-maintenance': 'home-repairs-maintenance',
  'plumbing-electrical': 'plumbing-electrical',
  'construction-masonry': 'construction-masonry',
  'cleaning-services': 'cleaning-services',
  'moving-removal': 'moving-removal',
  'pest-control': 'pest-control',
  'security-services': 'security-services',
  'tailoring-alterations': 'tailoring-alterations',
  'ac-refrigeration': 'ac-refrigeration',
  'solar-power': 'solar-power',
  'water-pumps': 'water-pumps',
  'aluminium-glass': 'aluminium-glass',
  'welding-fabrication': 'welding-fabrication',
});

export const getBusinessSubcategoryMap = () => ({
  'business-industry': null,
  'machinery-equipment': 'machinery-equipment',
  'industrial-tools': 'industrial-tools',
  'manufacturing-supplies': 'manufacturing-supplies',
  'office-equipment': 'office-equipment',
  'wholesale-bulk-goods': 'wholesale-bulk-goods',
  'logistics-transport': 'logistics-transport',
  'franchises': 'franchises',
  'business-for-sale': 'business-for-sale',
});

export const getFoodAgriSubcategoryMap = () => ({
  'food-agriculture': null,
  'fresh-produce': 'fresh-produce',
  'dairy-eggs': 'dairy-eggs',
  'meat-poultry': 'meat-poultry',
  'seafood': 'seafood',
  'processed-foods': 'processed-foods',
  'bakery-confectionery': 'bakery-confectionery',
  'beverages': 'beverages',
  'organic-specialty': 'organic-specialty',
  'bulk-wholesale': 'bulk-wholesale',
  'farm-supplies': 'farm-supplies',
  'agricultural-equipment': 'agricultural-equipment',
  'seeds-plants': 'seeds-plants',
  'livestock': 'livestock',
  'catering-services': 'catering-services',
  'food-trucks-stalls': 'food-trucks-stalls',
});

export const getEducationSubcategoryMap = () => ({
  'education-tuition': null,
  'school-tuition': 'school-tuition',
  'university-coaching': 'university-coaching',
  'professional-courses': 'professional-courses',
  'language-learning': 'language-learning',
  'music-arts-education': 'music-arts-education',
  'stem-coding': 'stem-coding',
  'exam-preparation': 'exam-preparation',
  'special-needs-education': 'special-needs-education',
  'adult-education': 'adult-education',
  'tutoring-services': 'tutoring-services',
});

export const getSportsSubcategoryMap = () => ({
  'sports-fitness': null,
  'sports-fitness-hobbies': null,
  'sports-equipment': 'sports-equipment',
  'fitness-gym': 'fitness-gym',
  'outdoor-camping': 'outdoor-camping',
  'cycling-equipment': 'cycling-equipment',
  'fishing-hunting': 'fishing-hunting',
  'yoga-wellness': 'yoga-wellness',
  'board-games-puzzles': 'board-games-puzzles',
  'creative-hobbies': 'creative-hobbies',
});

// Fashion service category mapping
export const getFashionServiceCategory = (slug) => {
  const categoryMap = {
    'womens-clothing': 'tailoring-alterations',
    'mens-clothing': 'tailoring-mens',
    'kids-clothing': 'tailoring-kids',
    'traditional-wear': 'tailoring-traditional',
    'shoes-footwear': 'shoe-repair',
    'bags-accessories': 'bag-repair',
    'beauty-skincare': 'beauty-services',
    'beauty-personal-care': 'beauty-services',
    'perfumes-cosmetics': 'beauty-services',
    'fashion-beauty': 'tailoring-alterations',
  };
  return categoryMap[slug?.toLowerCase()] || 'tailoring-alterations';
};

// Convert slug to readable name
export const slugToDisplayName = (slug, categories = []) => {
  if (!slug || slug === 'all') return 'All Ads';
  
  // Check main categories
  const mainCat = categories.find(c => c.slug === slug);
  if (mainCat) return mainCat.name;
  
  // Check subcategories
  for (const cat of categories) {
    if (cat.subcategories) {
      const subCat = cat.subcategories.find(s => s.slug === slug);
      if (subCat) return subCat.name;
    }
  }
  
  // Fallback: convert slug to readable name
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Jobs category slugs
export const JOBS_SLUGS = [
  'jobs', 'technology-it', 'office-admin', 'finance-accounting',
  'healthcare-care', 'education-training', 'sales-marketing',
  'customer-service', 'driver-delivery', 'hospitality-tourism',
  'skilled-trades', 'construction-property', 'manufacturing-warehouse',
  'retail', 'creative-media', 'cleaning-domestic', 'security', 'freelance-gigs',
];

export const isJobsCategory = (slug) => JOBS_SLUGS.includes(slug?.toLowerCase());

// Get subcategory for a given slug using the mapping functions
export const getSubcategory = (slug, subParam = '', selectedSubcategory = '') => {
  if (subParam) return subParam;
  if (selectedSubcategory) return selectedSubcategory;
  
  const normalizedSlug = slug?.toLowerCase();
  
  // Check each mapping
  const tradesMap = getTradesSubcategoryMap();
  if (normalizedSlug in tradesMap) return tradesMap[normalizedSlug];
  
  const businessMap = getBusinessSubcategoryMap();
  if (normalizedSlug in businessMap) return businessMap[normalizedSlug];
  
  const foodAgriMap = getFoodAgriSubcategoryMap();
  if (normalizedSlug in foodAgriMap) return foodAgriMap[normalizedSlug];
  
  const sportsMap = getSportsSubcategoryMap();
  if (normalizedSlug in sportsMap) return sportsMap[normalizedSlug];
  
  const musicMap = getMusicSubcategoryMap();
  if (normalizedSlug in musicMap) return musicMap[normalizedSlug];
  
  const educationMap = getEducationSubcategoryMap();
  if (normalizedSlug in educationMap) return educationMap[normalizedSlug];
  
  return null;
};

// Category names mapping for display
export const CATEGORY_NAMES = {
  'electronics': 'Electronics',
  'mobile-phones': 'Mobile Phones',
  'computers-tablets': 'Computers & Tablets',
  'tvs-entertainment': 'TVs & Entertainment',
  'cameras': 'Cameras',
  'audio': 'Audio',
  'gaming': 'Gaming',
  'vehicles': 'Vehicles',
  'cars': 'Cars',
  'motorcycles': 'Motorcycles',
  'vans': 'Vans',
  'trucks': 'Trucks',
  'buses-coaches': 'Buses & Coaches',
  'property': 'Property',
  'property-for-rent': 'Property for Rent',
  'property-for-sale': 'Property for Sale',
  'rooms-shared': 'Rooms & Shared',
  'commercial': 'Commercial Property',
  'fashion-beauty': 'Fashion & Beauty',
  'jobs': 'Jobs',
  'services-trades': 'Services & Trades',
  'business-industry': 'Business & Industry',
  'food-agriculture': 'Food & Agriculture',
  'sports-fitness': 'Sports & Fitness',
  'education-tuition': 'Education & Tuition',
  'baby-kids': 'Baby & Kids',
  'pets-animals': 'Pets & Animals',
  'musical-instruments': 'Musical Instruments',
  'books-media': 'Books & Media',
  'community-local-life': 'Community & Local Life',
  'events-functions': 'Events & Functions',
};

export const getCategoryDisplayName = (slug) => {
  if (!slug) return 'All Ads';
  return CATEGORY_NAMES[slug?.toLowerCase()] || slugToDisplayName(slug);
};

